import type { ComponentConfig } from '@puckeditor/core'

/**
 * The CSS `display` values offered per component. `''` = leave as-is (the
 * component's own/CSS display). `none` hides the component on the active
 * breakpoint (pruned from the resolved tree in production).
 */
export const DISPLAY_OPTIONS = [
  { label: 'Default', value: '' },
  { label: 'Block', value: 'block' },
  { label: 'Flex', value: 'flex' },
  { label: 'Inline Flex', value: 'inline-flex' },
  { label: 'Grid', value: 'grid' },
  { label: 'Inline Block', value: 'inline-block' },
  { label: 'Hidden', value: 'none' },
] as const

export const displayField = {
  type: 'select' as const,
  label: 'Display',
  options: DISPLAY_OPTIONS as unknown as { label: string; value: string }[],
}

type AnyFields = Record<string, unknown>

/**
 * Adds the responsive `display` field to a component config. Applied to every
 * registered component (see `createPuckConfig`) so any component can be
 * shown/hidden or have its display type overridden per breakpoint. Idempotent;
 * render is untouched (display is applied by the layout wrapper + resolve()).
 *
 * Components with `resolveFields` (e.g. anything wrapped in withLayout) rebuild
 * their field set on the fly, so we also patch `resolveFields` to keep `display`
 * present — otherwise it'd be dropped for those components.
 */
export function withResponsive<C extends ComponentConfig<any>>(config: C): C {
  if (!config || (config.fields as AnyFields | undefined)?.display) {
    return config
  }

  const innerResolveFields = config.resolveFields as
    | ((data: unknown, params: unknown) => AnyFields | Promise<AnyFields>)
    | undefined

  const resolveFields = innerResolveFields
    ? (data: unknown, params: unknown) => {
        const resolved = innerResolveFields(data, params)
        return resolved instanceof Promise
          ? resolved.then(f => ({ ...f, display: displayField }))
          : { ...resolved, display: displayField }
      }
    : config.resolveFields

  return {
    ...config,
    fields: { ...(config.fields ?? {}), display: displayField },
    defaultProps: { display: '', ...(config.defaultProps ?? {}) },
    ...(resolveFields ? { resolveFields } : {}),
  } as C
}
